import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

/**
 * iOS-style 3D Wheel Picker Component
 * - Infinite circular scrolling (never ends)
 * - Auto-select when scrolling stops
 * - No cancel/done buttons
 */
const WheelPicker3D = ({
  items = [],
  selectedIndex = 0,
  onSelect,
  onClose,
  onScrollChange, // Callback to sync external UI with scroll state
  renderItem,
  itemHeight = 48,
  visibleItems = 7,
  width = 150,
  height = 200,
  title = "",
  autoCloseDelay = 800, // Auto close after this delay when scroll stops
  initialTouchY = null, // Pass the Y position when opening from an ongoing touch
}) => {
  const [scrollOffset, setScrollOffset] = useState(() => -selectedIndex * itemHeight);
  const [isScrolling, setIsScrolling] = useState(false);

  const containerRef = useRef(null);
  const touchStartY = useRef(null);
  const lastTouchY = useRef(null);
  const lastTouchTime = useRef(null);
  const momentumAnimationRef = useRef(null);
  const snapAnimationRef = useRef(null);
  const autoCloseTimerRef = useRef(null);
  const velocityHistory = useRef([]);
  const isAnimating = useRef(false);
  const lastSelectedIndex = useRef(selectedIndex);

  // Ref to track latest scrollOffset synchronously (avoids stale closures in document-level handlers)
  const scrollOffsetRef = useRef(-selectedIndex * itemHeight);

  // Function refs - updated on every render so document-level handlers always call the latest versions
  const snapToNearestItemRef = useRef(null);
  const applyMomentumRef = useRef(null);

  // Prop refs - so scheduleAutoClose always calls the latest callbacks/data
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  const itemsRef = useRef(items);

  const halfVisible = Math.floor(visibleItems / 2);
  const wheelRadius = (visibleItems * itemHeight) / Math.PI;
  const totalItems = items.length;

  // Get wrapped index for infinite scroll
  const getWrappedIndex = useCallback((index) => {
    if (totalItems === 0) return 0;
    return ((index % totalItems) + totalItems) % totalItems;
  }, [totalItems]);

  // Calculate offset for index
  const getOffsetForIndex = useCallback((index) => {
    return -index * itemHeight;
  }, [itemHeight]);

  // Calculate index from offset (can be negative or > totalItems for infinite scroll)
  const getIndexFromOffset = useCallback((offset) => {
    return Math.round(-offset / itemHeight);
  }, [itemHeight]);

  // Haptic feedback
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(1);
    }
  }, []);

  // Auto close and select - uses refs to always get the latest scroll position, callbacks, and data
  const scheduleAutoClose = useCallback(() => {
    clearTimeout(autoCloseTimerRef.current);
    autoCloseTimerRef.current = setTimeout(() => {
      const currentIndex = getWrappedIndex(getIndexFromOffset(scrollOffsetRef.current));
      if (currentIndex !== lastSelectedIndex.current) {
        lastSelectedIndex.current = currentIndex;
        if (onSelectRef.current) {
          onSelectRef.current(currentIndex, itemsRef.current[currentIndex]);
        }
      }
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    }, autoCloseDelay);
  }, [autoCloseDelay, getIndexFromOffset, getWrappedIndex]);

  // Snap to nearest item with smooth spring animation
  const snapToNearestItem = useCallback((fromOffset, targetVelocity = 0) => {
    cancelAnimationFrame(momentumAnimationRef.current);
    cancelAnimationFrame(snapAnimationRef.current);
    isAnimating.current = true;

    // Project where scroll would end with momentum
    let projectedOffset = fromOffset;
    if (Math.abs(targetVelocity) > 1) {
      const deceleration = 0.92;
      let v = targetVelocity;
      let iterations = 0;
      while (Math.abs(v) > 0.5 && iterations < 50) {
        projectedOffset += v;
        v *= deceleration;
        iterations++;
      }
    }

    const targetIndex = getIndexFromOffset(projectedOffset);
    const targetOffset = getOffsetForIndex(targetIndex);

    // Smooth ease-out animation (no overshoot — approaches target asymptotically)
    let currentOffset = fromOffset;
    const easeSpeed = 0.15; // Higher = faster snap

    const animate = () => {
      const displacement = targetOffset - currentOffset;

      // Lerp towards target (exponential ease-out, never overshoots)
      currentOffset += displacement * easeSpeed;

      scrollOffsetRef.current = currentOffset;
      setScrollOffset(currentOffset);

      if (Math.abs(displacement) < 0.5) {
        scrollOffsetRef.current = targetOffset;
        setScrollOffset(targetOffset);
        setIsScrolling(false);
        isAnimating.current = false;
        triggerHaptic();
        scheduleAutoClose();
        return;
      }

      snapAnimationRef.current = requestAnimationFrame(animate);
    };

    snapAnimationRef.current = requestAnimationFrame(animate);
  }, [getIndexFromOffset, getOffsetForIndex, scheduleAutoClose, triggerHaptic]);

  // Apply momentum scrolling (infinite - no bounds)
  const applyMomentum = useCallback((initialVelocity) => {
    cancelAnimationFrame(momentumAnimationRef.current);
    cancelAnimationFrame(snapAnimationRef.current);
    isAnimating.current = true;

    let currentVelocity = initialVelocity;
    const deceleration = 0.96;
    const minVelocity = 0.8;
    let currentOffset = scrollOffsetRef.current;
    let lastIndex = getIndexFromOffset(currentOffset);

    const animate = () => {
      if (Math.abs(currentVelocity) < minVelocity) {
        snapToNearestItem(currentOffset, currentVelocity);
        return;
      }

      currentOffset += currentVelocity;
      currentVelocity *= deceleration;

      // Trigger haptic on index change
      const newIndex = getIndexFromOffset(currentOffset);
      if (newIndex !== lastIndex) {
        triggerHaptic();
        lastIndex = newIndex;
      }

      scrollOffsetRef.current = currentOffset;
      setScrollOffset(currentOffset);
      momentumAnimationRef.current = requestAnimationFrame(animate);
    };

    momentumAnimationRef.current = requestAnimationFrame(animate);
  }, [getIndexFromOffset, snapToNearestItem, triggerHaptic]);

  // Initialize
  useEffect(() => {
    const offset = getOffsetForIndex(selectedIndex);
    scrollOffsetRef.current = offset;
    setScrollOffset(offset);
    lastSelectedIndex.current = selectedIndex;
  }, [selectedIndex, getOffsetForIndex]);

  // Report scroll changes to parent for syncing external UI
  useEffect(() => {
    if (onScrollChange) {
      const currentIndex = getWrappedIndex(getIndexFromOffset(scrollOffset));
      const fractionalOffset = (-scrollOffset / itemHeight) % 1;
      onScrollChange({
        currentIndex,
        scrollOffset,
        fractionalOffset,
        isScrolling,
        items,
      });
    }
  }, [scrollOffset, isScrolling, onScrollChange, getWrappedIndex, getIndexFromOffset, itemHeight, items]);

  // Keep function refs updated on every render (avoids stale closures in document-level handlers)
  snapToNearestItemRef.current = snapToNearestItem;
  applyMomentumRef.current = applyMomentum;

  // Keep prop refs updated on every render (avoids stale closures in scheduleAutoClose)
  onSelectRef.current = onSelect;
  onCloseRef.current = onClose;
  itemsRef.current = items;

  // Initialize touch state from initialTouchY prop (for seamless scroll takeover)
  // When opened mid-touch, the browser keeps routing touch events to the original
  // element (not this component's DOM). We use document-level listeners to capture
  // the ongoing gesture until the finger lifts.
  useEffect(() => {
    if (initialTouchY === null) return;

    touchStartY.current = initialTouchY;
    lastTouchY.current = initialTouchY;
    lastTouchTime.current = performance.now();
    velocityHistory.current = [];
    setIsScrolling(true);

    // Document-level handlers to capture the ongoing touch gesture
    const handleDocTouchMove = (e) => {
      if (touchStartY.current === null) return;
      e.preventDefault();

      const touch = e.touches[0];
      const currentTime = performance.now();
      const deltaY = touch.clientY - lastTouchY.current;
      const deltaTime = currentTime - lastTouchTime.current;

      if (deltaTime > 0) {
        const instantVelocity = deltaY / deltaTime * 16;
        velocityHistory.current.push({ velocity: instantVelocity, time: currentTime });
        velocityHistory.current = velocityHistory.current.filter(
          sample => currentTime - sample.time < 100
        );
      }

      // Update both ref and state synchronously
      setScrollOffset(prev => {
        const newOffset = prev + deltaY;
        scrollOffsetRef.current = newOffset;
        return newOffset;
      });

      lastTouchY.current = touch.clientY;
      lastTouchTime.current = currentTime;
    };

    const handleDocTouchEnd = () => {
      // Clean up document listeners - subsequent touches will use the component's own handlers
      document.removeEventListener('touchmove', handleDocTouchMove);
      document.removeEventListener('touchend', handleDocTouchEnd);
      document.removeEventListener('touchcancel', handleDocTouchEnd);

      if (touchStartY.current === null) return;

      let avgVelocity = 0;
      if (velocityHistory.current.length > 0) {
        const now = performance.now();
        let totalWeight = 0;
        let weightedSum = 0;

        velocityHistory.current.forEach(sample => {
          const age = now - sample.time;
          const weight = Math.max(0, 1 - age / 100);
          weightedSum += sample.velocity * weight;
          totalWeight += weight;
        });

        if (totalWeight > 0) {
          avgVelocity = weightedSum / totalWeight;
        }
      }

      touchStartY.current = null;
      lastTouchY.current = null;
      velocityHistory.current = [];

      // Use function refs to call the LATEST versions (not stale mount-time closures)
      if (Math.abs(avgVelocity) > 3) {
        applyMomentumRef.current(avgVelocity);
      } else {
        snapToNearestItemRef.current(scrollOffsetRef.current, avgVelocity);
      }
    };

    document.addEventListener('touchmove', handleDocTouchMove, { passive: false });
    document.addEventListener('touchend', handleDocTouchEnd);
    document.addEventListener('touchcancel', handleDocTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleDocTouchMove);
      document.removeEventListener('touchend', handleDocTouchEnd);
      document.removeEventListener('touchcancel', handleDocTouchEnd);
    };
  }, []); // Only run once on mount

  // Cleanup
  useEffect(() => {
    return () => {
      cancelAnimationFrame(momentumAnimationRef.current);
      cancelAnimationFrame(snapAnimationRef.current);
      clearTimeout(autoCloseTimerRef.current);
    };
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback((e) => {
    cancelAnimationFrame(momentumAnimationRef.current);
    cancelAnimationFrame(snapAnimationRef.current);
    clearTimeout(autoCloseTimerRef.current);
    isAnimating.current = false;

    const touch = e.touches[0];
    touchStartY.current = touch.clientY;
    lastTouchY.current = touch.clientY;
    lastTouchTime.current = performance.now();
    velocityHistory.current = [];
    setIsScrolling(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartY.current === null) return;
    e.preventDefault();

    const touch = e.touches[0];
    const currentTime = performance.now();
    const deltaY = touch.clientY - lastTouchY.current;
    const deltaTime = currentTime - lastTouchTime.current;

    if (deltaTime > 0) {
      const instantVelocity = deltaY / deltaTime * 16;
      velocityHistory.current.push({ velocity: instantVelocity, time: currentTime });
      velocityHistory.current = velocityHistory.current.filter(
        sample => currentTime - sample.time < 100
      );
    }

    // Infinite scroll - no bounds!
    setScrollOffset(prev => {
      const newOffset = prev + deltaY;
      scrollOffsetRef.current = newOffset;
      return newOffset;
    });

    lastTouchY.current = touch.clientY;
    lastTouchTime.current = currentTime;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchStartY.current === null) return;

    let avgVelocity = 0;
    if (velocityHistory.current.length > 0) {
      const now = performance.now();
      let totalWeight = 0;
      let weightedSum = 0;

      velocityHistory.current.forEach(sample => {
        const age = now - sample.time;
        const weight = Math.max(0, 1 - age / 100);
        weightedSum += sample.velocity * weight;
        totalWeight += weight;
      });

      if (totalWeight > 0) {
        avgVelocity = weightedSum / totalWeight;
      }
    }

    touchStartY.current = null;
    lastTouchY.current = null;
    velocityHistory.current = [];

    if (Math.abs(avgVelocity) > 3) {
      applyMomentum(avgVelocity);
    } else {
      snapToNearestItem(scrollOffsetRef.current, avgVelocity);
    }
  }, [applyMomentum, snapToNearestItem]);

  // Mouse wheel handler
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    clearTimeout(autoCloseTimerRef.current);

    if (isAnimating.current) {
      cancelAnimationFrame(momentumAnimationRef.current);
      cancelAnimationFrame(snapAnimationRef.current);
    }

    setIsScrolling(true);

    const delta = -e.deltaY * 0.8;
    const newOffset = scrollOffsetRef.current + delta;
    scrollOffsetRef.current = newOffset;
    setScrollOffset(newOffset);

    clearTimeout(containerRef.current?.wheelTimeout);
    containerRef.current.wheelTimeout = setTimeout(() => {
      snapToNearestItem(newOffset, 0);
    }, 120);
  }, [snapToNearestItem]);

  // Get currently selected index (wrapped)
  const currentSelectedIndex = useMemo(() => {
    return getWrappedIndex(getIndexFromOffset(scrollOffset));
  }, [scrollOffset, getIndexFromOffset, getWrappedIndex]);

  // Render items with infinite circular 3D transforms
  const renderWheelItems = useMemo(() => {
    const rendered = [];
    const centerLogicalIndex = -scrollOffset / itemHeight;

    // Render enough items to fill visible area + buffer
    const renderRange = halfVisible + 2;

    for (let offset = -renderRange; offset <= renderRange; offset++) {
      const logicalIndex = Math.round(centerLogicalIndex) + offset;
      const actualIndex = getWrappedIndex(logicalIndex);
      const item = items[actualIndex];

      if (!item) continue;

      const offsetFromCenter = logicalIndex - centerLogicalIndex;
      const absOffset = Math.abs(offsetFromCenter);

      // Skip if too far
      if (absOffset > halfVisible + 1.5) continue;

      // 3D rotation
      const maxAngle = 70;
      const angle = Math.min(Math.max(offsetFromCenter * (maxAngle / (halfVisible + 0.5)), -maxAngle), maxAngle);
      const radians = (angle * Math.PI) / 180;

      const translateY = Math.sin(radians) * wheelRadius;
      const translateZ = (Math.cos(radians) - 1) * wheelRadius * 0.5;

      const normalizedDistance = Math.min(absOffset / (halfVisible + 0.5), 1);
      const scale = 1 - normalizedDistance * 0.2;
      const opacity = Math.max(0, 1 - normalizedDistance * 0.7);

      const isSelected = absOffset < 0.5;

      rendered.push(
        <div
          key={`${logicalIndex}-${actualIndex}`}
          className="absolute w-full flex items-center justify-center"
          style={{
            height: `${itemHeight}px`,
            transform: `
              translateY(${translateY}px)
              translateZ(${translateZ}px)
              rotateX(${-angle}deg)
              scale(${scale})
            `,
            opacity,
            zIndex: Math.round(100 - absOffset * 10),
            transition: isScrolling ? 'none' : 'all 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)',
            pointerEvents: 'none',
            willChange: 'transform, opacity',
          }}
        >
          {renderItem ? (
            renderItem(item, actualIndex, isSelected)
          ) : (
            <span
              className={`text-center px-4 py-2 rounded-xl transition-all duration-150 ${isSelected
                  ? 'text-white font-semibold bg-[rgb(0,76,80)]'
                  : 'text-gray-500'
                }`}
              style={{
                fontSize: `${isSelected ? 18 : Math.max(14, 16 - absOffset * 2)}px`,
              }}
            >
              {typeof item === 'object' ? item.label || item.symbol || item.name : item}
            </span>
          )}
        </div>
      );
    }

    return rendered;
  }, [items, scrollOffset, itemHeight, halfVisible, wheelRadius, isScrolling, renderItem, getWrappedIndex]);

  // Handle backdrop click - close immediately
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      clearTimeout(autoCloseTimerRef.current);
      const currentIndex = getWrappedIndex(getIndexFromOffset(scrollOffsetRef.current));
      if (currentIndex !== lastSelectedIndex.current && onSelectRef.current) {
        onSelectRef.current(currentIndex, itemsRef.current[currentIndex]);
      }
      onCloseRef.current?.();
    }
  }, [getIndexFromOffset, getWrappedIndex]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center md:hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      style={{
        animation: 'wheelPickerFadeIn 0.1s ease-out',
        touchAction: 'none',
      }}
    >
      <style>
        {`
          @keyframes wheelPickerFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes wheelPickerSlideUp {
            from {
              opacity: 0;
              transform: translateY(20px) scale(0.97);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>

      <div
        className="bg-white/90 rounded-3xl overflow-hidden"
        style={{
          width: `${width}px`,
          animation: 'wheelPickerSlideUp 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
          boxShadow: '0 0px 5px rgba(0, 0, 0, 0.01), 0 8px 25px rgba(0, 0, 0, 0.10)',
        }}
      >
        {/* Title bar */}
        {title && (
          <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
            <h3 className="text-center text-sm font-semibold text-gray-600 tracking-wide">{title}</h3>
          </div>
        )}

        {/* Wheel container */}
        <div
          ref={containerRef}
          className="relative overflow-hidden"
          style={{
            height: `${height}px`,
            perspective: '1200px',
            perspectiveOrigin: '50% 50%',
          }}
        >
          {/* Top gradient fade */}
          <div
            className="absolute top-0 left-0 right-0 z-20 pointer-events-none"
            style={{
              height: '35%',
              background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
            }}
          />

          {/* Bottom gradient fade */}
          <div
            className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none"
            style={{
              height: '35%',
              background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
            }}
          />

          {/* Selection indicator */}
          <div
            className="absolute left-4 right-4 z-10 pointer-events-none rounded-2xl"
            style={{
              top: '50%',
              transform: 'translateY(-50%)',
              height: `${itemHeight + 10}px`,
              // background: 'linear-gradient(135deg, rgba(0,76,80,0.06) 0%, rgba(0,76,80,0.1) 100%)',
              // border: '1.5px solid rgba(0,76,80,0.12)',
            }}
          />

          {/* 3D Wheel */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transformStyle: 'preserve-3d',
            }}
          >
            {renderWheelItems}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WheelPicker3D;
